import React from 'react';
import {
  ArrowDownRight, ArrowUpRight, Loader2, Minus, Stethoscope, Wrench,
} from 'lucide-react';

// "It worked for the last 10 days and now it doesn't — what changed?"
// The backend walks the funnel (delivery -> clicks -> auction price -> cost per lead
// -> lead quality -> selling) and returns the FIRST stage that moved. This panel just
// renders that verdict honestly, including how much of the recent window's money has
// realistically arrived yet.

const TONE = {
  scale: 'bg-emerald-50 text-emerald-800 border-emerald-300',
  keep: 'bg-slate-50 text-slate-700 border-slate-300',
  watch: 'bg-amber-50 text-amber-800 border-amber-300',
  fix_creative: 'bg-amber-50 text-amber-800 border-amber-300',
  auction_costlier: 'bg-amber-50 text-amber-800 border-amber-300',
  fix_targeting: 'bg-rose-50 text-rose-800 border-rose-300',
  fix_followup: 'bg-rose-50 text-rose-800 border-rose-300',
  no_sales: 'bg-rose-50 text-rose-800 border-rose-300',
  losing_money: 'bg-rose-50 text-rose-800 border-rose-300',
  delivery_stalled: 'bg-rose-50 text-rose-800 border-rose-300',
  no_leads_tracked: 'bg-rose-50 text-rose-800 border-rose-300',
  too_new: 'bg-sky-50 text-sky-800 border-sky-300',
  too_little_data: 'bg-slate-50 text-slate-600 border-slate-300',
};

// Metrics where UP is bad, so the arrow colour cannot just follow the sign.
const LOWER_IS_BETTER = new Set(['cpm', 'cpc', 'cpl', 'cpr', 'projected_cpr', 'frequency']);

const ROWS = [
  { key: 'spend', label: 'Spend', kind: 'money' },
  { key: 'impressions', label: 'Impressions', kind: 'count' },
  { key: 'reach', label: 'People reached', kind: 'count' },
  { key: 'frequency', label: 'Times each person saw it', kind: 'raw' },
  { key: 'ctr', label: 'CTR', kind: 'pct' },
  { key: 'cpm', label: 'Cost per 1,000 views', kind: 'money' },
  { key: 'leads', label: 'Leads', kind: 'count' },
  { key: 'cpl', label: 'Cost per lead', kind: 'money' },
  { key: 'buyers', label: 'Sales so far', kind: 'count' },
  { key: 'projected_cpr', label: 'Cost per sale (projected)', kind: 'money' },
  { key: 'revenue', label: 'Revenue so far', kind: 'money' },
];

const Delta = ({ value, metric }) => {
  if (value === null || value === undefined) {
    return <span className="text-gray-400 inline-flex items-center gap-1"><Minus className="h-3 w-3" />—</span>;
  }
  const up = value > 0;
  const good = LOWER_IS_BETTER.has(metric) ? !up : up;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 font-medium ${good ? 'text-emerald-600' : 'text-rose-600'}`}>
      <Icon className="h-3.5 w-3.5" />
      {Math.round(Math.abs(value) * 10) / 10}%
    </span>
  );
};

const WINDOW_CHOICES = [7, 10, 14, 30];

const WindowPicker = ({ days, onDaysChange, adId }) => {
  if (!onDaysChange) return null;
  return (
    <div className="ml-auto inline-flex rounded-lg border border-slate-300 bg-white p-0.5"
      data-testid={`ad-doctor-window-picker-${adId}`}>
      {WINDOW_CHOICES.map((d) => (
        <button
          key={d}
          type="button"
          onClick={(e) => { e.stopPropagation(); onDaysChange(d); }}
          className={`rounded-md px-2 py-0.5 text-xs font-medium transition-colors ${
            days === d ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
          data-testid={`ad-doctor-window-${d}-${adId}`}
        >
          {d}d
        </button>
      ))}
    </div>
  );
};

export const AdDoctorPanel = ({
  diagnosis, days, maturity, loading, formatCurrency, spendAvailable = true, adId,
  onDaysChange,
}) => {
  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-xl border bg-white p-4 text-sm text-gray-500"
        data-testid={`ad-doctor-loading-${adId}`}>
        <Loader2 className="h-4 w-4 animate-spin" />
        Comparing the last {days} days with the {days} before that…
      </div>
    );
  }

  if (!diagnosis) {
    return (
      <div className="rounded-xl border bg-white p-4" data-testid={`ad-doctor-empty-${adId}`}>
        <div className="flex flex-wrap items-center gap-2">
          <Stethoscope className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-500">
            No spend or leads for this ad in either window, so there is nothing to diagnose.
          </span>
          <WindowPicker days={days} onDaysChange={onDaysChange} adId={adId} />
        </div>
      </div>
    );
  }

  const fmt = (value, kind) => {
    if (value === null || value === undefined) return '—';
    if (kind === 'money') return formatCurrency(value);
    if (kind === 'pct') return `${value}%`;
    if (kind === 'count') return Number(value).toLocaleString('en-IN');
    return value;
  };

  const recent = diagnosis.recent || {};
  const prior = diagnosis.prior || {};
  const tone = TONE[diagnosis.verdict] || TONE.watch;

  return (
    <div className="rounded-xl border bg-white p-4" data-testid={`ad-doctor-${adId}`}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Stethoscope className="h-4 w-4 text-gray-400" />
        <h4 className="text-sm font-semibold text-gray-900">
          Last {days} days vs the {days} before
        </h4>
        <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-bold ${tone}`}
          data-testid={`ad-doctor-verdict-${adId}`}>
          {diagnosis.verdict_label}
        </span>
        <WindowPicker days={days} onDaysChange={onDaysChange} adId={adId} />
      </div>

      <p className="text-sm text-gray-800 mb-3" data-testid={`ad-doctor-headline-${adId}`}>
        {diagnosis.headline}
      </p>

      {/* The one line that answers "is this spend justified" without a spreadsheet. */}
      {recent.projected_cpr > 0 && recent.avg_order_value > 0 && (
        <div className={`mb-3 rounded-lg border px-3 py-2 text-sm ${
          recent.projected_cpr > recent.avg_order_value
            ? 'border-rose-200 bg-rose-50 text-rose-900'
            : 'border-emerald-200 bg-emerald-50 text-emerald-900'}`}
          data-testid={`ad-doctor-economics-${adId}`}>
          A sale costs <strong>{formatCurrency(recent.projected_cpr)}</strong> and brings{' '}
          <strong>{formatCurrency(recent.avg_order_value)}</strong> —{' '}
          {recent.projected_cpr > recent.avg_order_value
            ? <>a loss of <strong>{formatCurrency(recent.projected_cpr - recent.avg_order_value)}</strong> per sale</>
            : <>a gain of <strong>{formatCurrency(recent.avg_order_value - recent.projected_cpr)}</strong> per sale</>}
          {recent.maturity_pct < 99 && (
            <span className="ml-1 opacity-80">
              (allowing for the {100 - Math.round(recent.maturity_pct)}% of this window's
              money that normally arrives later)
            </span>
          )}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={`ad-doctor-table-${adId}`}>
          <thead>
            <tr className="text-xs uppercase tracking-wider text-gray-500">
              <th className="text-left font-semibold py-1.5">Metric</th>
              <th className="text-right font-semibold py-1.5">
                Earlier {days}d
                <span className="block font-normal normal-case text-gray-400">{prior.from} → {prior.to}</span>
              </th>
              <th className="text-right font-semibold py-1.5">
                Last {days}d
                <span className="block font-normal normal-case text-gray-400">{recent.from} → {recent.to}</span>
              </th>
              <th className="text-right font-semibold py-1.5">Change</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {ROWS.filter((r) => spendAvailable
              || !['spend', 'cpm', 'cpc', 'cpl', 'projected_cpr'].includes(r.key)).map((r) => (
              <tr key={r.key} data-testid={`ad-doctor-row-${adId}-${r.key}`}>
                <td className="py-1.5 text-gray-700">{r.label}</td>
                <td className="py-1.5 text-right tabular-nums text-gray-600">{fmt(prior[r.key], r.kind)}</td>
                <td className="py-1.5 text-right tabular-nums font-medium text-gray-900">{fmt(recent[r.key], r.kind)}</td>
                <td className="py-1.5 text-right"><Delta value={diagnosis.deltas?.[r.key]} metric={r.key} /></td>
              </tr>
            ))}
            <tr data-testid={`ad-doctor-row-${adId}-quality`}>
              <td className="py-1.5 text-gray-700">Leads you cannot serve</td>
              <td className="py-1.5 text-right tabular-nums text-gray-600">{prior.out_of_area_pct ?? 0}%</td>
              <td className="py-1.5 text-right tabular-nums font-medium text-gray-900">{recent.out_of_area_pct ?? 0}%</td>
              <td className="py-1.5 text-right">
                <Delta
                  value={Math.round(((recent.out_of_area_pct ?? 0) - (prior.out_of_area_pct ?? 0)) * 10) / 10}
                  metric="cpl"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {(diagnosis.reasons || []).length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">What moved</p>
          <ul className="space-y-0.5 text-sm text-gray-700">
            {diagnosis.reasons.map((reason, i) => (
              <li key={i} data-testid={`ad-doctor-reason-${adId}-${i}`}>
                <span className="font-medium">{reason.metric}</span>
                {reason.prior !== null && reason.prior !== undefined && (
                  <> {reason.unit === '₹' ? formatCurrency(reason.prior) : reason.prior}
                    {reason.unit === '%' ? '%' : ''} → </>
                )}
                <span className="font-semibold">
                  {reason.unit === '₹' ? formatCurrency(reason.recent) : reason.recent}
                  {reason.unit === '%' ? '%' : ''}
                </span>
                {reason.note ? <span className="text-gray-500"> — {reason.note}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(diagnosis.actions || []).length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1">
            <Wrench className="h-3 w-3" /> What to do
          </p>
          <ul className="list-disc pl-5 space-y-0.5 text-sm text-gray-700">
            {diagnosis.actions.map((action, i) => (
              <li key={i} data-testid={`ad-doctor-action-${adId}-${i}`}>{action}</li>
            ))}
          </ul>
        </div>
      )}

      {maturity?.sample_payments > 0 && (
        <p className="mt-3 text-xs text-gray-500" data-testid={`ad-doctor-maturity-${adId}`}>
          Projections use Wisedrive's own measured payment curve: of every 100 sales an
          ad eventually makes, about {Math.round((maturity.by_day?.['0'] || 0) * 100)} land
          on day one, {Math.round((maturity.by_day?.['7'] || 0) * 100)} by day 7 and{' '}
          {Math.round((maturity.by_day?.['30'] || 0) * 100)} by day 30
          ({maturity.sample_payments} payments measured).
        </p>
      )}
    </div>
  );
};

export default AdDoctorPanel;
